import React from 'react';
import {
    SimpleShowLayout,
    TabbedForm,
    FormTab,
    Toolbar
} from 'admin-on-rest';
import Show from '../admin/ShowInventoryTransfer';
import Create from '../admin/Create';
import NewTransferLocationsSelectInput from '../components/NewTransferLocationsSelectInput';
import TransferDetail from '../components/TransferDetail';
import TransferTable from '../components/TransferTable';
import TransferButtons from '../components/TransferButtons';
import SaveButtonForTransfer from '../buttons/SaveButtonForTransfer';
import NewTransferProductUnitTable from '../components/NewTransferProductUnitTable';
import NewTransferCollectionTable from '../components/NewTransferCollectionTable';
import TooltipIcon from '../components/TooltipIcon';
import ReactTooltip from 'react-tooltip';
import {viewStyles} from '../shared/styles-view';
import Avatar from 'material-ui/Avatar';
import IncomingIcon from 'material-ui/svg-icons/action/assignment-returned';
import ReceivedIcon from 'material-ui/svg-icons/action/assignment-turned-in';
import TransferredProductsIcon from 'material-ui/svg-icons/action/description';
import {CardActions} from "material-ui/Card";
import ListButton from "../buttons/ListButton";

export const fuseIncomingTransferIcon = IncomingIcon;
export const fuseReceivedTransferIcon = ReceivedIcon;
export const fuseTransferredProductsIcon = TransferredProductsIcon;

// Handle event from item Add
const handleAddTransferItem = (e, index, value) => {
    //console.log('Add ' + e + ' to transfer_items');
};

// Handle event from item Delete
const handleDeleteTransferItem = (e) => {
    // console.log('Delete ' + e + ' from transfer_items');
};

/**
 * Generate a form and table of Product Unit Cases
 *
 * @param record
 * @return {*}
 * @constructor
 */
const MakeNewTransferProductUnitTable = ({record}) => {
    const transferItems = (record.transfer_items) ? record.transfer_items : [];
    return (
        <NewTransferProductUnitTable
            {...record}
            transferItems={transferItems}
            onAddUpdate={handleAddTransferItem}
            onDeleteUpdate={handleDeleteTransferItem}
        />
    )
};

/**
 * Generate a form and table of Collections (Repacks or Retail Boxes)
 *
 * @param record
 * @param type
 * @return {*}
 * @constructor
 */
const MakeNewTransferCollectionTable = ({record, type}) => {
    const transferItems = (record.transfer_items) ? record.transfer_items : [];
    return (
        <NewTransferCollectionTable
            {...record}
            collectionType={type}
            transferItems={transferItems}
            onAddUpdate={handleAddTransferItem}
            onDeleteUpdate={handleDeleteTransferItem}
            perPage={100}
        />
    )
};

// Replace Actions for Show Incoming/Received Transfers
const ShowActions = ({basePath, data}) => (
    <CardActions style={viewStyles.actions}>
        <ListButton basePath="/InventoryTransfers" label="List Incoming"/>
        <ListButton basePath="/ReceivedInventoryTransfers" label="List Received"/>
        <ListButton basePath="/InventoryTransfersByMemo" label="List Transfers by Memo"/>
    </CardActions>
);

/*
 * View: Show (Incoming)
 */

const ShowIncomingTitle = () => {
    return <span> </span>;
};

const ShowIncomingDescription = () => {
    return (
        <span>
            <p>Review Incoming Transfer details below. Each is a transfer of sellable units due to arrive at the
                selected Location.</p>
            <p>A transfer may contain Product Units, Repacks, or Retail Boxes. Repacks and Retail Boxes have contents.
                Click their <strong>Toggle</strong> icon to view their contents.</p>
        </span>
    );
};

/*
 * Show Incoming Transfers Table
 */
export const fuseIncomingTransferShow = (props) => {
    return (
        <Show title={<ShowIncomingTitle/>} actions={<ShowActions/>} {...props}>
            <SimpleShowLayout>
                <TransferDetail description={<ShowIncomingDescription/>} {...props}/>
                <TransferTable {...props}/>
                <TransferButtons {...props}/>
            </SimpleShowLayout>
        </Show>
    )
};

export const fuseIncomingTransferShowViewOnly = (props) => {
    return (
        <Show title={<ShowIncomingTitle/>} actions={<ShowActions/>} {...props}>
            <SimpleShowLayout>
                <TransferDetail description={<ShowIncomingDescription/>} {...props}/>
                <TransferTable {...props}/>
            </SimpleShowLayout>
        </Show>
    )
};

/*
 * View: Show (Received)
 */

const ShowReceivedTitle = () => {
    return <span> </span>;
};

const ShowReceivedDescription = () => {
    return (
        <span>
            <p>Review Received Transfer details below. Each is a transfer of sellable units marked as having arrived at
                the selected Location.</p>
            <p>A transfer may contain Product Units, Repacks, or Retail Boxes. Repacks and Retail Boxes have contents.
                Click their <strong>Toggle</strong> icon to view their contents.</p>
        </span>
    );
};

/*
 * Show Received Transfers Table
 */
export const fuseReceivedTransferShow = (props) => (
    <Show title={<ShowReceivedTitle/>} actions={<ShowActions/>} {...props}>
        <SimpleShowLayout>
            <TransferDetail description={<ShowReceivedDescription/>} {...props}/>
            <TransferTable {...props}/>
        </SimpleShowLayout>
    </Show>
);

/*
 * View: Show (by Memo)
 */

const ShowByMemoTitle = ({record}) => {
    return <span> </span>;
};

const ShowByMemoDescription = () => {
    return (
        <span>
            <p>Review Transfer details below. Each is a transfer of sellable units marked in transit or having arrived
                at a Location.</p>
            <p>A transfer may contain Product Units, Repacks, or Retail Boxes. Repacks and Retail Boxes have contents.
                Click their <strong>Toggle</strong> icon to view their contents.</p>
        </span>
    );
};

/*
 * Show Transfers by Memo Table
 */
export const fuseTransferByMemoShow = (props) => (
    <Show title={<ShowByMemoTitle/>} actions={<ShowActions/>} {...props}>
        <SimpleShowLayout>
            <TransferDetail description={<ShowByMemoDescription/>} {...props}/>
            <TransferTable {...props}/>
        </SimpleShowLayout>
    </Show>
);

/*
 * View: Create form
 */

// Set title for Create Transfer
const CreateTitle = <span><Avatar icon={<IncomingIcon/>} style={viewStyles.avatar}
                                  className="avatar-title"/>Create an Inventory Transfer</span>;

const CreateDescription = () => {
    return (
        <span>
            <p>Create a transfer of Inventory from one Location to another. A transfer may
                contain <strong>Repacks</strong>, <strong>Retail Boxes</strong>, or cases
                of <strong>Product Units</strong>.</p>
            <p>Enter the required fields in each Tab, and add transfer contents as needed.</p>
        </span>
    );
};

const CreateToolbar = props => <Toolbar {...props} >
    <SaveButtonForTransfer submitOnEnter={false}/>
</Toolbar>;

// Set defaults for Create form
const defaultCreateValues = {
    location_from: null,
    location_to: null,
    inv_transfer_model: {},
    transfer_items: []
};

/*
 * Create Transfer form (unused)
 * @TODO requires updating New Transfer Table data to combine Repacks and Retail Boxes
 */
export const fuseTransferCreate = (props) => (
    <Create {...props} title={CreateTitle} desc={<CreateDescription/>}>
        <TabbedForm defaultValue={defaultCreateValues} submitOnEnter={false} toolbar={<CreateToolbar/>}>

            {/* Create Step: Locations */}
            <FormTab label="Locations" style={viewStyles.row}>
                <span style={viewStyles.fieldHeader}>
                    Select From/To Locations
                </span>
                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
                <span style={viewStyles.fieldHeaderDescription}>
                    Select a <em>source</em> Location the Inventory comes from, and a <em>destination</em> Location
                    to transfer the Inventory to.
                    <br/><br/>
                    Both Locations must have a DBA of the same Product Type. After selecting the source Location, the
                    available options for the destination will change to eligible Locations.
                </span>
                <NewTransferLocationsSelectInput/>

                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

                <span style={viewStyles.fieldInstructions}>
                    Upon completing this Tab, click the Add Inventory Tab to proceed.
                </span>
                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
            </FormTab>

            {/* Create Step: Add Inventory */}
            <FormTab label="Add Inventory" style={viewStyles.row}>

                <span style={viewStyles.fieldHeader}>
                    Add Inventory: Product Unit Cases
                    <TooltipIcon dataFor="ttAddProduct"/>
                    <ReactTooltip id="ttAddProduct" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        <span style={viewStyles.fieldHeaderDescription}>
                            Collections of <strong>Product Units</strong> may be added to a Transfer.
                            Only <em>Cases</em> may be added; <em>Pieces</em> and <em>Mid</em> units may not be added
                            to a Transfer.
                            <br/><br/>
                            To add Product Units, click the <em>Add Product Unit</em> button and select a Case item.
                            <br/><br/>
                            To remove a Product Unit Case, click its <em>Delete</em> icon.
                        </span>
                    </ReactTooltip>
                </span>
                {<MakeNewTransferProductUnitTable/>}

                <hr style={viewStyles.divider}/>

                <span style={viewStyles.fieldHeader}>
                    Add Inventory: Repacks
                    <TooltipIcon dataFor="ttAddRepack"/>
                    <ReactTooltip id="ttAddRepack" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        <span style={viewStyles.fieldHeaderDescription}>
                            Collections of Product Units may be assembled in a <strong>Repack</strong> for the
                            purposes of returning unsold inventory. Each Repack may be added only once.
                            <br/><br/>
                            To add Repacks, click the <em>Add Repack</em> button. Browse through the available Repacks
                            and expand to view their contents, then click the Add icon.
                            <br/><br/>
                            To remove a Repack, click its <em>Delete</em> icon.
                        </span>
                    </ReactTooltip>
                </span>
                {<MakeNewTransferCollectionTable type="repack"/>}

                <hr style={viewStyles.divider}/>

                <span style={viewStyles.fieldHeader}>
                    Add Inventory: Retail Boxes
                    <TooltipIcon dataFor="ttAddRetailBox"/>
                    <ReactTooltip id="ttAddRetailBox" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        <span style={viewStyles.fieldHeaderDescription}>
                            Collections of Product Units may be assembled in a <strong>Retail Box</strong>. Multiple
                            quantities of Retail Boxes may be added.
                            <br/><br/>
                            To add a Retail Box, enter the Quantity you wish to add. Browse through the available
                            Retail Boxes and expand to view their contents, then click the Add icon.
                            <br/><br/>
                            To remove a Retail Box, click its <em>Delete</em> icon.
                        </span>
                    </ReactTooltip>
                </span>
                {<MakeNewTransferCollectionTable type="retail_box"/>}

                <hr style={viewStyles.divider}/>

                <span style={viewStyles.fieldInstructions}>
                    After clicking <strong>Save</strong>, you will be redirected to the Inventory list.
                </span>
                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
            </FormTab>

        </TabbedForm>
    </Create>
);



// WEBPACK FOOTER //
// ./src/Fuse/Inventory/transfer.js